import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { auditLogsResponseSchema, type AuditLogRow } from '@/features/traceability/types';
import { useApi } from '@/lib/api-context';

import {
  deadlinesResponseSchema,
  normComplianceResponseSchema,
  pendingSignaturesResponseSchema,
  summaryResponseSchema,
  type Deadlines,
  type NormCompliance,
  type PendingSignatures,
  type Summary,
} from './types';

// Consultas del Panel del QMS (so=430, spec §4). La empresa activa va en la posición 2
// del queryKey (nunca se sirve la caché de otra empresa) y cada respuesta pasa por Zod.
// El resumen comparte clave con la barra de salud del menú: una sola consulta.

const BASE = '/api/v1/quality/dashboard';

/** Permiso de las tarjetas nuevas del panel (so=410 D1). */
export const DASHBOARD_PERMISSION = 'quality:master-list:read';
export const DEADLINE_WINDOW = 15;
export const DEADLINE_ROWS = 8;
export const PENDING_ROWS = 5;
export const ACTIVITY_ROWS = 5;

export interface Scope {
  companyId: string | null;
  /** Permiso concedido para la tarjeta. */
  enabled: boolean;
}

function useParsed<T>(
  key: string,
  url: string,
  parse: (raw: unknown) => T,
  { companyId, enabled }: Scope,
): UseQueryResult<T> {
  const api = useApi();
  return useQuery({
    queryKey: ['sgc-dashboard', companyId, key, url],
    queryFn: async () => parse(await api.get<unknown>(url)),
    enabled: enabled && companyId !== null,
    staleTime: 60_000,
  });
}

export function useDashboardSummary(scope: Scope): UseQueryResult<Summary> {
  return useParsed('summary', `${BASE}/summary`, (r) => summaryResponseSchema.parse(r).data, scope);
}

export function useDeadlines(scope: Scope): UseQueryResult<Deadlines> {
  return useParsed(
    'deadlines',
    `${BASE}/deadlines?window=${DEADLINE_WINDOW}&limit=${DEADLINE_ROWS}`,
    (r) => deadlinesResponseSchema.parse(r).data,
    scope,
  );
}

export function usePendingSignatures(scope: Scope): UseQueryResult<PendingSignatures> {
  return useParsed(
    'pending-signatures',
    `${BASE}/pending-signatures?limit=${PENDING_ROWS}`,
    (r) => pendingSignaturesResponseSchema.parse(r).data,
    scope,
  );
}

export function useNormCompliance(scope: Scope): UseQueryResult<NormCompliance> {
  return useParsed(
    'norm-compliance',
    `${BASE}/norm-compliance`,
    (r) => normComplianceResponseSchema.parse(r).data,
    scope,
  );
}

/** Últimos eventos de la bitácora (el hook de Trazabilidad pagina de 50 en 50). */
export function useRecentActivity(scope: Scope): UseQueryResult<AuditLogRow[]> {
  return useParsed(
    'activity',
    `/api/v1/audit-logs?limit=${ACTIVITY_ROWS}`,
    (r) => auditLogsResponseSchema.parse(r).data,
    scope,
  );
}
